import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n";

const anchors = [
  ["home", "#home"],
  ["original", "#original"],
  ["tapestry", "#tapestry"],
  ["about", "#about"],
  ["aboutIt", "#about-it"],
  ["expo", "#expo"],
  ["contact", "#contact"],
];

export function scrollTo(hash) {
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: 0, duration: 1.4 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollTo(hash), open ? 350 : 0);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-linen/85 backdrop-blur-md">
        <div className="flex items-center justify-between px-5 md:px-10 py-4">
          <a
            href="#home"
            onClick={(e) => go(e, "#home")}
            data-testid="nav-logo"
            className="font-serif font-medium uppercase text-lg md:text-xl tracking-tight hover:text-klein transition-colors duration-300 flex items-center gap-3"
          >
            JEANNE LEVEQUE
            <img src="/images/signature.png" alt="" data-testid="nav-signature" className="h-5 md:h-6 w-auto" />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {anchors.slice(1).map(([key, hash]) => (
              <a
                key={key}
                href={hash}
                onClick={(e) => go(e, hash)}
                data-testid={`nav-link-${key}`}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60 hover:text-klein transition-colors duration-300"
              >
                {t.nav[key]}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="lang-toggle">
              <button
                data-testid="lang-toggle-fr"
                onClick={() => setLang("fr")}
                className={`transition-colors duration-300 ${lang === "fr" ? "text-klein" : "text-black/40 hover:text-black"}`}
              >
                FR
              </button>
              <span className="mx-1.5 text-black/30">/</span>
              <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={`transition-colors duration-300 ${lang === "en" ? "text-klein" : "text-black/40 hover:text-black"}`}
              >
                EN
              </button>
            </div>
            <button
              data-testid="menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden text-black hover:text-klein transition-colors duration-300"
              aria-label="Open menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-linen flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
              <span className="font-serif text-lg">JEANNE LEVEQUE</span>
              <button
                data-testid="menu-close"
                onClick={() => setOpen(false)}
                className="hover:text-klein transition-colors duration-300"
                aria-label="Close menu"
              >
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {anchors.map(([key, hash], i) => (
                <motion.a
                  key={key}
                  href={hash}
                  onClick={(e) => go(e, hash)}
                  data-testid={`mobile-nav-link-${key}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif font-light uppercase text-4xl sm:text-5xl leading-tight hover:text-klein transition-colors duration-300"
                >
                  {t.nav[key]}
                </motion.a>
              ))}
            </nav>
            <div className="px-8 py-6 border-t border-black/10 font-mono text-[11px] uppercase tracking-[0.2em] text-black/50">
              jeanne.leveque.m@gmail.com
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
